import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, Users, Eye } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 15000;

/**
 * FamilyPulseCard — public, fan-facing "you're not alone" snapshot.
 * Mirrors the LIVE SITE ACTIVITY reference: three KPI tiles on a soft, dotted
 * card with a green live pulse. Lives near the homepage hero to make every
 * new visitor feel the room.
 */
const formatNumber = (n) => {
  if (n == null) return '–';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(0)}K`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
};

const PulseTile = ({ icon: Icon, value, label, testid, accent = '#0F766E' }) => (
  <div
    className="flex-1 min-w-0 flex flex-col items-center text-center px-4 py-5"
    data-testid={testid}
  >
    <div
      className="w-14 h-14 rounded-full flex items-center justify-center mb-3"
      style={{ backgroundColor: 'rgba(15, 118, 110, 0.10)' }}
    >
      <Icon size={22} strokeWidth={1.75} style={{ color: accent }} />
    </div>
    <div className="font-['Bebas_Neue'] text-4xl md:text-5xl text-[#0D0D0D] leading-none tracking-tight">
      {formatNumber(value)}
    </div>
    <div className="mt-2 text-[11px] md:text-xs uppercase tracking-[0.18em] text-[#737373] font-medium">
      {label}
    </div>
  </div>
);

const FamilyPulseCard = ({ className = '' }) => {
  const [stats, setStats] = useState({
    today_live_visitors: 1,
    week_unique_visitors: null,
    all_time_page_views: null,
  });

  useEffect(() => {
    let alive = true;
    const fetchStats = async () => {
      try {
        const { data } = await axios.get(`${API}/api/presence/family-pulse`);
        if (alive) setStats(data);
      } catch (_) {
        /* keep last value */
      }
    };
    fetchStats();
    const t = setInterval(fetchStats, POLL_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  return (
    <div
      className={`relative w-full max-w-2xl mx-auto rounded-2xl overflow-hidden shadow-[0_20px_60px_-30px_rgba(0,0,0,0.6)] ${className}`}
      data-testid="family-pulse-card"
      style={{
        background:
          'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.06) 1px, transparent 0) 0 0/14px 14px, #FAFAF7',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-center gap-2 pt-5 pb-2">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
        </span>
        <span className="text-[11px] md:text-xs uppercase tracking-[0.22em] text-[#0D0D0D]/70 font-semibold">
          Live Site Activity
        </span>
      </div>

      {/* KPI Row */}
      <div className="flex items-stretch divide-x divide-black/5 pb-4">
        <PulseTile
          icon={Activity}
          value={stats.today_live_visitors}
          label="viewers right now"
          testid="pulse-live"
        />
        <PulseTile
          icon={Users}
          value={stats.week_unique_visitors}
          label="visitors this week"
          testid="pulse-week"
        />
        <PulseTile
          icon={Eye}
          value={stats.all_time_page_views}
          label="all-time page views"
          testid="pulse-pageviews"
        />
      </div>

      {/* Family vibe footer */}
      <div className="text-center pb-4 px-4">
        <p className="text-[11px] text-[#737373] italic">
          You're not alone — join the family.
        </p>
      </div>
    </div>
  );
};

export default FamilyPulseCard;
